import React, { useMemo } from 'react';
import './MentionAutocomplete.css';

/**
 * Maximum number of participants to show in the dropdown.
 */
export const MAX_MENTION_ITEMS = 8;

/**
 * Filter and sort participants for mention autocomplete.
 * Exported so MessageInput can use the same logic for keyboard selection.
 *
 * @param {Array} participants - Array of participant objects
 * @param {string} filter - Filter text (case-insensitive)
 * @returns {Array} Filtered and sorted participants (max MAX_MENTION_ITEMS)
 */
export function filterMentionParticipants(participants, filter) {
  if (!participants || !Array.isArray(participants)) {
    return [];
  }

  const filterLower = filter.toLowerCase();

  // Filter by name (case-insensitive)
  let filtered = participants.filter((p) => {
    if (!p.name) return false;
    const nameLower = p.name.toLowerCase();
    // Match prefix first, then substring
    return nameLower.startsWith(filterLower) || nameLower.includes(filterLower);
  });

  // Sort: prefix matches first, then humans before LLMs, then alphabetically
  filtered.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aIsPrefix = aName.startsWith(filterLower);
    const bIsPrefix = bName.startsWith(filterLower);

    // Prefix matches come first
    if (aIsPrefix && !bIsPrefix) return -1;
    if (!aIsPrefix && bIsPrefix) return 1;

    // Then humans before LLMs
    const aIsHuman = a.participant_type === 'human';
    const bIsHuman = b.participant_type === 'human';
    if (aIsHuman && !bIsHuman) return -1;
    if (!aIsHuman && bIsHuman) return 1;

    // Then alphabetical
    return aName.localeCompare(bName);
  });

  // Limit to max items
  return filtered.slice(0, MAX_MENTION_ITEMS);
}

/**
 * MentionAutocomplete - Dropdown for selecting participants when typing @mentions.
 *
 * @param {Object} props
 * @param {Array} props.participants - Array of participant objects from the channel
 * @param {string} props.filter - Current filter text (typed after @)
 * @param {number} props.selectedIndex - Currently selected index for keyboard navigation
 * @param {Function} props.onSelect - Callback when participant is selected: (participant) => void
 * @param {Object} props.position - Position for dropdown: { top, left }
 */
export function MentionAutocomplete({
  participants,
  filter,
  selectedIndex,
  onSelect,
  position,
}) {
  // Filter and sort participants using shared utility
  const filteredParticipants = useMemo(() => {
    return filterMentionParticipants(participants, filter);
  }, [participants, filter]);

  // Handle click on participant
  const handleClick = (participant, e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(participant);
  };

  // Don't render if no matches
  if (filteredParticipants.length === 0) {
    return (
      <div
        className="mention-autocomplete"
        style={{ top: position?.top, left: position?.left }}
      >
        <div className="mention-autocomplete-empty">
          No participants match
        </div>
      </div>
    );
  }

  return (
    <div
      className="mention-autocomplete"
      style={{ top: position?.top, left: position?.left }}
    >
      {filteredParticipants.map((participant, index) => (
        <div
          key={participant.id}
          className={`mention-autocomplete-item ${
            index === selectedIndex ? 'selected' : ''
          }`}
          onClick={(e) => handleClick(participant, e)}
          onMouseDown={(e) => e.preventDefault()} // Prevent textarea blur
        >
          <span className="mention-autocomplete-avatar">
            {participant.name?.charAt(0).toUpperCase() || '?'}
          </span>
          <span className="mention-autocomplete-name">
            {participant.name}
          </span>
          {participant.role === 'consultant' && (
            <span className="mention-autocomplete-type type-consultant">
              Consult
            </span>
          )}
          <span
            className={`mention-autocomplete-type ${
              participant.participant_type === 'llm' ? 'type-llm' : 'type-human'
            }`}
          >
            {participant.participant_type === 'llm' ? 'AI' : 'H'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default MentionAutocomplete;
